import { Box, Stack, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import React from 'react';

import StudyReportScrollNavHeader from './StudyReportScrollNavHeader';
import { buildStudyReportSectionNavigation, buildStudyReportSectionRows } from './StudyReportSectionNavigation';
import StudyReportSectionNumberBadge from './StudyReportSectionNumberBadge';

export interface StudyReportLayoutSection {
  id: string;
  title: string;
  content: React.ReactNode;
  numberLabel?: string;
  navParentId?: string;
  navParentTitle?: string;
  rowGroupId?: string;
  rowColumns?: number;
  rowGroupTitle?: React.ReactNode;
  rowGroupNumberLabel?: React.ReactNode;
  rowGroupActions?: React.ReactNode;
}

export interface StudyReportScrollNavAttachmentInsets {
  top?: number;
}

interface Props {
  reportTitle: string;
  scrollSpyAnchor?: React.ReactNode;
  topContent?: React.ReactNode;
  sections: StudyReportLayoutSection[];
  emptyState?: React.ReactNode;
  contentSpacing?: number;
  sectionSpacing?: number;
  sectionsContainerSx?: SxProps<Theme>;
  scrollNavAttachmentInsets?: StudyReportScrollNavAttachmentInsets;
}

const StudyReportSectionsLayout: React.FC<Props> = ({
  reportTitle,
  scrollSpyAnchor,
  topContent,
  sections,
  emptyState,
  contentSpacing = 3,
  sectionSpacing = 2,
  sectionsContainerSx,
  scrollNavAttachmentInsets,
}) => {
  const layoutRootRef = React.useRef<HTMLDivElement>(null);
  const scrollSpyAnchorRef = React.useRef<HTMLDivElement>(null);
  const scrollNavRef = React.useRef<HTMLDivElement>(null);
  const [scrollContainerEl, setScrollContainerEl] = React.useState<HTMLElement | null>(null);
  const [scrollNavHorizontalInsets, setScrollNavHorizontalInsets] = React.useState({ left: 0, right: 0 });
  const [isScrollNavStuck, setIsScrollNavStuck] = React.useState(false);
  const sectionContainerRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const [activeSectionId, setActiveSectionId] = React.useState<string | null>(null);
  const navigation = React.useMemo(() => buildStudyReportSectionNavigation(sections), [sections]);
  const sectionRows = React.useMemo(() => buildStudyReportSectionRows(sections), [sections]);
  const anchorContent = scrollSpyAnchor || topContent;
  const supplementalTopContent = scrollSpyAnchor ? topContent : null;
  const resolvedScrollNavAttachmentInsets = React.useMemo(
    () => ({
      top: scrollNavAttachmentInsets?.top ?? 0,
      left: isScrollNavStuck ? scrollNavHorizontalInsets.left : 0,
      right: isScrollNavStuck ? scrollNavHorizontalInsets.right : 0,
    }),
    [isScrollNavStuck, scrollNavAttachmentInsets, scrollNavHorizontalInsets.left, scrollNavHorizontalInsets.right]
  );

  const getScrollAlignmentOffset = React.useCallback(() => {
    const navHeight = scrollNavRef.current?.getBoundingClientRect().height || 0;
    return navHeight + 8;
  }, []);

  const refreshScrollNavHorizontalInsets = React.useCallback(() => {
    const layoutRootEl = layoutRootRef.current;

    if (!scrollContainerEl || !layoutRootEl) {
      setScrollNavHorizontalInsets((currentInsets) =>
        currentInsets.left === 0 && currentInsets.right === 0 ? currentInsets : { left: 0, right: 0 }
      );
      return;
    }

    const scrollContainerRect = scrollContainerEl.getBoundingClientRect();
    const scrollContainerParentRect = scrollContainerEl.parentElement?.getBoundingClientRect();
    const hasParentBoundary = Boolean(scrollContainerParentRect && scrollContainerParentRect.width > 0);
    const targetLeft = hasParentBoundary
      ? Math.max(scrollContainerRect.left, scrollContainerParentRect?.left ?? scrollContainerRect.left)
      : scrollContainerRect.left;
    const targetRight = hasParentBoundary
      ? Math.min(scrollContainerRect.right, scrollContainerParentRect?.right ?? scrollContainerRect.right)
      : scrollContainerRect.right;
    const layoutRootRect = layoutRootEl.getBoundingClientRect();
    const nextInsets = {
      left: Math.max(0, Math.round(layoutRootRect.left - targetLeft)),
      right: Math.max(0, Math.round(targetRight - layoutRootRect.right)),
    };

    setScrollNavHorizontalInsets((previousInsets) => {
      if (previousInsets.left === nextInsets.left && previousInsets.right === nextInsets.right) {
        return previousInsets;
      }

      return nextInsets;
    });
  }, [scrollContainerEl]);

  const refreshScrollNavStuckState = React.useCallback(() => {
    const scrollNavEl = scrollNavRef.current;

    if (!scrollNavEl) {
      setIsScrollNavStuck(false);
      return;
    }

    const scrollNavRect = scrollNavEl.getBoundingClientRect();
    const topInset = scrollNavAttachmentInsets?.top ?? 0;
    const stickyTop = scrollContainerEl ? scrollContainerEl.getBoundingClientRect().top - topInset : -topInset;
    const nextIsScrollNavStuck = scrollNavRect.top <= stickyTop + 1;

    setIsScrollNavStuck((previousIsScrollNavStuck) =>
      previousIsScrollNavStuck === nextIsScrollNavStuck ? previousIsScrollNavStuck : nextIsScrollNavStuck
    );
  }, [scrollContainerEl, scrollNavAttachmentInsets?.top]);

  const refreshScrollState = React.useCallback(() => {
    let containerTop = getScrollAlignmentOffset();
    if (scrollContainerEl) {
      const containerRect = scrollContainerEl.getBoundingClientRect();
      const paddingTop = parseFloat(getComputedStyle(scrollContainerEl).paddingTop) || 0;
      containerTop =
        containerRect.top + (resolvedScrollNavAttachmentInsets.top > 0 ? 0 : paddingTop) + getScrollAlignmentOffset();
    }

    let bestSectionId: string | null = null;
    let bestDistance = Infinity;

    sectionContainerRefs.current.forEach((element, sectionId) => {
      const rect = element.getBoundingClientRect();

      if (rect.bottom > containerTop) {
        const distance = Math.abs(rect.top - containerTop);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestSectionId = sectionId;
        }
      }
    });

    setActiveSectionId(bestSectionId);
  }, [getScrollAlignmentOffset, resolvedScrollNavAttachmentInsets.top, scrollContainerEl]);

  React.useEffect(() => {
    let scrollContainer: HTMLElement | Window = window;
    const anchorElement = scrollSpyAnchorRef.current || sectionContainerRefs.current.values().next().value || null;

    if (anchorElement) {
      let currentElement: HTMLElement | null = anchorElement.parentElement;

      while (currentElement) {
        const { overflowY } = getComputedStyle(currentElement);
        if (overflowY === 'auto' || overflowY === 'scroll') {
          scrollContainer = currentElement;
          setScrollContainerEl(currentElement);
          break;
        }
        currentElement = currentElement.parentElement;
      }
    }

    const handleScroll = () => {
      refreshScrollNavStuckState();
      refreshScrollState();
    };
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    refreshScrollNavStuckState();
    refreshScrollState();

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [anchorContent, refreshScrollNavStuckState, refreshScrollState, sections]);

  React.useLayoutEffect(() => {
    refreshScrollNavStuckState();
    refreshScrollNavHorizontalInsets();

    const observedElements = [scrollContainerEl, layoutRootRef.current].filter((element): element is HTMLElement =>
      Boolean(element)
    );
    const refreshScrollNavLayout = () => {
      refreshScrollNavStuckState();
      refreshScrollNavHorizontalInsets();
    };
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(refreshScrollNavLayout);

    observedElements.forEach((element) => resizeObserver?.observe(element));
    window.addEventListener('resize', refreshScrollNavLayout);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', refreshScrollNavLayout);
    };
  }, [anchorContent, refreshScrollNavHorizontalInsets, refreshScrollNavStuckState, scrollContainerEl, sections.length]);

  const handleScrollToSection = React.useCallback(
    (sectionId: string) => {
      const targetSection = sectionContainerRefs.current.get(sectionId);

      if (!targetSection) {
        return;
      }

      setActiveSectionId(sectionId);

      const scrollOffset = getScrollAlignmentOffset();

      if (scrollContainerEl) {
        const containerRect = scrollContainerEl.getBoundingClientRect();
        const targetRect = targetSection.getBoundingClientRect();
        const paddingTop = parseFloat(getComputedStyle(scrollContainerEl).paddingTop) || 0;
        const nextTop =
          scrollContainerEl.scrollTop +
          (targetRect.top - containerRect.top) -
          (resolvedScrollNavAttachmentInsets.top > 0 ? 0 : paddingTop) -
          scrollOffset;

        scrollContainerEl.scrollTo({ top: Math.max(nextTop, 0), behavior: 'smooth' });
        return;
      }

      const nextTop = window.scrollY + targetSection.getBoundingClientRect().top - scrollOffset;
      window.scrollTo({ top: Math.max(nextTop, 0), behavior: 'smooth' });
    },
    [getScrollAlignmentOffset, resolvedScrollNavAttachmentInsets.top, scrollContainerEl]
  );

  const handleScrollToTop = React.useCallback(() => {
    if (scrollContainerEl) {
      scrollContainerEl.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [scrollContainerEl]);

  return (
    <Box ref={layoutRootRef} data-testid="study-report-sections-layout" sx={{ width: '100%', minWidth: 0 }}>
      <Stack spacing={contentSpacing} sx={{ minWidth: 0 }}>
        {anchorContent ? <Box ref={scrollSpyAnchorRef}>{anchorContent}</Box> : null}
        {supplementalTopContent ? <Box>{supplementalTopContent}</Box> : null}

        {sections.length > 0 ? (
          <StudyReportScrollNavHeader
            ref={scrollNavRef}
            visible
            reportTitle={reportTitle}
            sections={navigation.resolvedSections.map((section) => ({
              id: section.id,
              title: section.title,
              numberLabel: section.numberLabel,
            }))}
            tocItems={navigation.tocItems}
            activeSectionId={activeSectionId}
            onScrollToSection={handleScrollToSection}
            onScrollToTop={handleScrollToTop}
            attachmentInsets={resolvedScrollNavAttachmentInsets}
          />
        ) : null}

        <Stack spacing={sectionSpacing} sx={sectionsContainerSx}>
          {sections.length === 0 && emptyState
            ? emptyState
            : sectionRows.map((row) => {
                const firstSection = row.sections[0];
                const resolvedRowGroupTitle =
                  firstSection.rowGroupTitle ||
                  (firstSection.rowGroupId ? navigation.parentTitleById.get(firstSection.rowGroupId) : undefined) ||
                  firstSection.navParentTitle;
                const resolvedRowGroupNumberLabel =
                  firstSection.rowGroupNumberLabel ||
                  (firstSection.rowGroupId ? navigation.parentNumberLabelById.get(firstSection.rowGroupId) : undefined);
                const renderedSections = row.sections.map((section) => (
                  <Box
                    key={section.id}
                    data-testid={`study-report-section-anchor-${section.id}`}
                    ref={(element: HTMLDivElement | null) => {
                      if (element) {
                        sectionContainerRefs.current.set(section.id, element);
                      } else {
                        sectionContainerRefs.current.delete(section.id);
                      }
                    }}
                    sx={{ minWidth: 0, scrollMarginTop: '88px' }}
                  >
                    {section.content}
                  </Box>
                ));

                if (!row.rowGroupId && row.sections.length === 1) {
                  return renderedSections[0];
                }

                return (
                  <Stack key={row.key} spacing={sectionSpacing}>
                    {row.isFirstRowInGroup && resolvedRowGroupTitle ? (
                      <Box data-testid={`study-report-section-row-group-header-${row.rowGroupId || row.key}`}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1.5}
                          alignItems={{ xs: 'stretch', sm: 'center' }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                            {resolvedRowGroupNumberLabel ? (
                              <StudyReportSectionNumberBadge value={resolvedRowGroupNumberLabel} />
                            ) : null}
                            <Typography variant="h5" fontWeight={600} sx={{ minWidth: 0, flex: 1 }}>
                              {resolvedRowGroupTitle}
                            </Typography>
                          </Stack>
                          {firstSection.rowGroupActions ? <Box>{firstSection.rowGroupActions}</Box> : null}
                        </Stack>
                      </Box>
                    ) : null}
                    <Box
                      data-testid={`study-report-section-row-${row.key}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr)',
                        gap: sectionSpacing,
                        alignItems: 'start',
                        '@media (min-width:821px)': {
                          gridTemplateColumns: `repeat(${row.rowColumns}, minmax(0, 1fr))`,
                        },
                      }}
                    >
                      {renderedSections}
                    </Box>
                  </Stack>
                );
              })}
        </Stack>
      </Stack>
    </Box>
  );
};

export default StudyReportSectionsLayout;
